import React, { useState, useEffect, createContext } from 'react';
import { useLocation } from 'react-router-dom';
import "./HeaderCMS.css";
import HeaderCMSLinks from './HeaderCMSLinks';
import HeaderCMSNavigation from './HeaderCMSNavigation';
import HeaderCMSModals from '../../Modals/HeaderCMSModals/HeaderCMSModals';
import { SideMenu } from '../../Modals/SideMenu/SideMenu';
import * as translator from "../../../utils/translator";
import * as http from "../../../utils/http";
import { saveAppGlobalBannerFlag } from "../../../utils/appGlobalBanner";
import { clientConfig } from '../../../config';
import { useCartContext } from '../../../customHooks/useCart';

interface HeaderCMSProps {
  country: string;
  refreshData?: boolean;
}

type MenuLink = {
  text: string;
  url: string;
}
type NameAndEmail = {
  first_name:string
  last_name:string
  email:string
}
type PromotedProductItem = {
  id: number;
  name: string;
  price: number;
  price_before_discount: number;
  discount: number;
  imageLink: string;
  url: string;
}

type HeaderData = {
  menuLinks: Array<MenuLink>;
  rootCategoriesPairs: any;
  categoriesByRoot: any[];
  unreadNotifications: number;
  customerNameAndEmail:NameAndEmail;
  promotedProductItems: Array<PromotedProductItem>;
  shouldShowOTC: boolean;
  urlPrefix: string;
  rpCategories?: string[];
}

const TREATMENTS = "treatments";
const SEARCH = "search";
const HAMBURGER = "hamburger";
type ModalName = typeof TREATMENTS | typeof SEARCH | typeof HAMBURGER | "";

// Create the headerCMSContext outside of the component
export const headerCMSContext = createContext<any>(null);


const HeaderCMS: React.FC<HeaderCMSProps> = ({ country, refreshData }) => {
  const { pathname } = useLocation();
  const [currentOpenModal, setCurrentOpenModal] = useState<ModalName>("");
  const [headerData, setHeaderData] = useState<HeaderData | null>(null);
  const lastOrderCountry = (window as any).eventsTracking.getData('last_order_country')
  const isLastOrderSameCountry = !lastOrderCountry || !country ||
    lastOrderCountry.toUpperCase() === country.toUpperCase()
  if (!isLastOrderSameCountry) {
    (window as any).eventsTracking.setData('last_order', '');
    (window as any).eventsTracking.setData('last_order_country', '');
    (window as any).eventsStorage.addToCookie('last_order_country', '');
  }
  const lastOrder = (window as any).eventsTracking.getData("last_order")
  const lastPaidOrder = (window as any).eventsTracking.getData("last_paid_order")
  const cartID = (window as any).eventsTracking.getData("cart_id")
  const customerID = (window as any).eventsTracking.getData("customerID")
  const homeUrlPrefix = translator.getCMSUrlPrefixByLocale(translator.getLocaleByCountry(country));
  const language = translator.getLanguageByCountry(country)
  const [isCartSliderShown,setIsCartSliderShown] = useState(false)
  const [isSideMenuOpen,setIsSideMenuOpen] = useState(false)
  const [sideMenuOpenFrom,setSideMenuOpenFrom] = useState<string>('Header button')
  const { getCart } = useCartContext();
  const cart = getCart();

  useEffect(() => {
    http
      .getFromServer(`header/${country}/${language}`, [http.MIDDLEWARES.CUSTOMER_ID])
      .then((response) => {
        setHeaderData(response.data);
        saveAppGlobalBannerFlag(response.data?.showAppGlobalBanner);

      })
      .catch((error) => {
        window.clientLogger.error("Error in MyAccountLayout: ", error);
      })
      .finally(() => {

      });
  }, [refreshData]);



  const handleModals = (modalName: ModalName) => {
    if (modalName === HAMBURGER || modalName === TREATMENTS) {
        setSideMenuOpenFrom(modalName === TREATMENTS ? 'All Treatments button' : 'Header button');
        setIsSideMenuOpen(true);
    } else {
      setCurrentOpenModal((currentModal) =>
        currentModal === modalName ? "" : modalName
      );
    }
  };

  const handleCloseSideMenu = () => {
    setIsSideMenuOpen(false);
  };

  const redirectToCart = () =>{

    const isLastNotOrderPaid = (lastOrder && isLastOrderSameCountry && lastOrder !== lastPaidOrder) ? true : false;
  
    http
        .postToServer(`checkout/step`,
        {
          language: language,
          country: country.toLowerCase(),
          order_id: isLastNotOrderPaid ? lastOrder : null,
          customerID
        },
        [
          http.MIDDLEWARES.CART_ORDER,
        ])
        .then((response) => {
          window.location.href = "/" + response.data.resumePath;
        })
        .catch((error) => {
          window.clientLogger.error('fail in HeaderCMS/redirectToCart with error : ', error)
        })

  }


  const handleShoppingBagClick = () => {
    if(cart.products.length > 0){
      setIsCartSliderShown(true)
      //showCartModal()
    }else{
      redirectToCart()
    }
  }

  const getHeaderClassNameByModals = () => {
    if (currentOpenModal === HAMBURGER) {
      return "cms-header-container-disabled";
    } else if (currentOpenModal === "") {
      return "cms-header-container";
    } else {
      return "cms-header-container-modal-active";
    }
  };

  return (
    <headerCMSContext.Provider
      value={{
        handleModals,
        country,
        currentOpenModal,
        shoppingBagNotification: (cartID || (lastOrder && isLastOrderSameCountry && lastOrder !== lastPaidOrder)) ? true : false,
        myAccountTotalNotifications: headerData?.unreadNotifications || 0,
        headerData,
        redirectToCart,
        handleShoppingBagClick,
        isCartSliderShown,
        setIsCartSliderShown,
        language
      }}
    >
      <HeaderCMSModals
        cancelModal={() => setCurrentOpenModal("")}
      />

      <header className={getHeaderClassNameByModals()}>
        {/* This HeaderCMSNavigation is for mobile only */}
        <HeaderCMSNavigation isMobile={true} />
        {/* End of Mobile only HeaderCMSNavigation*/}

        <div
          className="doktorabc-logo-container"
          onClick={() => (window.location.href = clientConfig.MAIN_WEBSITE_URL + "/" + homeUrlPrefix)}
        >
          <img
            className="doktorabc-logo"
            src={currentOpenModal !== "" ? require("../../../images/logo-white.svg").default : require("../../../images/logo-dark.svg").default}
            alt={pathname === '/gb/account/ask-a-prescriber'
              ? 'DoktorABC - Online prescriber and pharmacies'
              : 'DoktorABC - Online doctor and pharmacies'}
          />
        </div>
        
        <HeaderCMSLinks menuLinks={headerData?.menuLinks || []} />
        <HeaderCMSNavigation />
        
      </header>

      <SideMenu headerData={headerData} isOpen={isSideMenuOpen} onClose={handleCloseSideMenu} language={language} country={country} menuOpenFrom={sideMenuOpenFrom}/>
    </headerCMSContext.Provider>
  );
};

export default HeaderCMS;